import { CanvasActionButton } from './CanvasActionButton';
import { CANVAS_AD_METADATA_HEIGHT } from '@shared/canvasGeometry';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { NodeProps } from '@xyflow/react';
import { Download, Trash2, ChevronLeft, ChevronRight, Monitor, Info, RefreshCw, Wrench, Code2, Play, Volume2, VolumeX, Copy, Film, Captions } from 'lucide-react';
import { useCreativeEditStore } from '@/pages/canvas/creative-edit-store';
import { CreativeEditNodeOverlay } from '@/pages/canvas/CreativeEditNodeOverlay';
import { useCreativeLayersStore } from '@/pages/canvas/creative-layers-store';
import { CreativeLayersNodeOverlay } from '@/pages/canvas/CreativeLayersNodeOverlay';
import { isHoverSoundEnabled, setHoverSoundEnabled } from '@/lib/hover-sound';
import { useCanvasStore } from '@/pages/canvas/canvas-store';
import { useToast } from '@/hooks/use-toast';
import { downloadCreatives } from '@/pages/canvas/download-creatives';
import { useImageRegeneration } from '@/pages/canvas/use-image-regeneration';
import { useActiveProjectImages, useActiveProjectQaBadges, useActiveProjectCanvasAdCopy } from '@/pages/canvas/store-selectors';
import { useAuthUser } from '@/App';
import { DebugSnapshotPopover } from '@/components/canvas/DebugSnapshotPopover';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
// Cycle-safe static import: the runner only pulls in canvas-store/-types +
// lib helpers, none of which import ImageNode (or anything that renders it).
import { videoCloneTileIndex, regenerateVideoCloneClip, regenerateVideoCloneFrame, restitchVideoCloneFinal, isVideoCloneFrameSrc } from "@/pages/canvas/video-clone-runner";

/**
 * v4.28 — QA badge + "i" info badge share ONE click-open popover with two
 * tabs (QA / Info). The old pair of hover popups overflowed and overlapped
 * ("all over the map" on long QA reasons). Click either circle to open on
 * its tab; click the X (or the circle again) to close. No hover-open.
 */
function AdMetaBadges({ src, imageId }: { src: string; imageId: string }) {
  const imageQaBadges = useActiveProjectQaBadges();
  const images = useActiveProjectImages();
  const [openTab, setOpenTab] = useState<null | 'qa' | 'info'>(null);

  const img = images.find((i) => i.id === imageId);
  const liveBadge = imageQaBadges[src];
  const persistedPassed = img?.qaPassed;
  const hasQa = !!liveBadge || (persistedPassed !== null && persistedPassed !== undefined);
  const isPassed = liveBadge ? liveBadge.passed : !!persistedPassed;
  const qaReason = liveBadge ? liveBadge.reason : (img?.qaReason ?? '');
  const hasInfo = !!img && !!(img.adStyle || img.adAngle || img.headline || img.ctaText || img.wavespeedPrompt || img.prompt || img.conceptName);

  if (!hasQa && !hasInfo) return null;

  const circle = (bg: string, border: string, child: React.ReactNode, testId: string, tab: 'qa' | 'info') => (
    <div
      data-testid={testId}
      className="nodrag"
      onClick={(e) => { e.stopPropagation(); setOpenTab((t) => (t === tab ? null : tab)); }}
      style={{
        width: 20, height: 20, borderRadius: '50%', background: bg,
        border, boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0, fontSize: 11, color: '#fff', fontWeight: 500, lineHeight: 1,
      }}
    >
      {child}
    </div>
  );

  const tabBtn = (tab: 'qa' | 'info', label: string) => (
    <button
      onClick={(e) => { e.stopPropagation(); setOpenTab(tab); }}
      style={{
        flex: 1, padding: '7px 0', fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3,
        background: 'none', border: 'none', cursor: 'pointer',
        color: openTab === tab ? '#fff' : '#9ca3af',
        borderBottom: openTab === tab ? '2px solid #6366f1' : '2px solid transparent',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ position: 'relative', display: 'flex', gap: 4, flexShrink: 0 }}>
      {hasQa && circle(isPassed ? '#22c55e' : '#ef4444', '1.5px solid #fff', isPassed ? '✓' : '✗', 'qa-badge', 'qa')}
      {hasInfo && circle('rgba(17,24,39,0.72)', '1.5px solid rgba(255,255,255,0.6)', <Info size={13} />, `ad-info-badge-${imageId}`, 'info')}

      {openTab && (
        <div
          data-testid={`ad-meta-popover-${imageId}`}
          className="nodrag nowheel"
          onMouseDown={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 28, right: 0, width: 300, maxHeight: 340,
            display: 'flex', flexDirection: 'column',
            background: 'rgba(17,24,39,0.96)', color: '#f3f4f6', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)', fontSize: 11.5, lineHeight: 1.5, zIndex: 40,
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.12)', flexShrink: 0 }}>
            {hasQa && tabBtn('qa', 'QA')}
            {hasInfo && tabBtn('info', 'Info')}
            <button
              onClick={(e) => { e.stopPropagation(); setOpenTab(null); }}
              aria-label="Close"
              style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '6px 10px', fontSize: 13, lineHeight: 1 }}
            >
              ✕
            </button>
          </div>
          <div style={{ padding: '10px 12px', overflowY: 'auto' }}>
            {openTab === 'qa' && hasQa && (() => {
              // v4.43 — parse "Text quality=9, Product accuracy=10, …" into
              // visual score rows (label left, colored n/10 right) instead of
              // a raw metric=X sentence. Falls back to plain text when the
              // reason isn't in scored form.
              const pairs = Array.from(qaReason.matchAll(/([A-Za-z][A-Za-z ]+?)\s*=\s*(\d+(?:\.\d+)?)/g))
                .map((m) => ({ label: m[1].trim().replace(/^Ai\b/i, 'AI'), score: parseFloat(m[2]) }));
              const overallM = qaReason.match(/overall\s+(\d+(?:\.\d+)?)\s*\/\s*10/i);
              const overall = overallM ? parseFloat(overallM[1]) : null;
              const scoreColor = (v: number) => (v >= 9 ? '#4ade80' : v >= 7 ? '#fbbf24' : '#f87171');
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: pairs.length ? 10 : 6 }}>
                    <div style={{ fontWeight: 700, color: isPassed ? '#4ade80' : '#f87171' }}>
                      {isPassed ? '✓ QA passed' : '✗ Needs review'}
                    </div>
                    {overall != null && (
                      <div style={{ fontWeight: 800, fontSize: 15, color: scoreColor(overall) }}>{overall}/10</div>
                    )}
                  </div>
                  {pairs.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {pairs.map((pr) => (
                        <div key={pr.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 9px', background: 'rgba(255,255,255,0.06)', borderRadius: 6 }}>
                          <span style={{ color: '#d1d5db', fontSize: 11.5 }}>{pr.label}</span>
                          <span style={{ fontWeight: 700, color: scoreColor(pr.score), fontVariantNumeric: 'tabular-nums' }}>{pr.score}/10</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#d1d5db' }}>
                      {qaReason || 'No details recorded.'}
                    </div>
                  )}
                </div>
              );
            })()}
            {openTab === 'info' && img && (
              <div>
                {img.conceptName && (
                  <div style={{ fontWeight: 700, fontSize: 12.5, color: '#e5e7eb', marginBottom: 6 }}>{img.conceptName}</div>
                )}
                {img.adStyle && (
                  <div style={{ marginBottom: 3 }}><span style={{ color: '#9ca3af', fontWeight: 600 }}>Style: </span><span>{img.adStyle}</span></div>
                )}
                {img.adAngle && (
                  <div style={{ marginBottom: 3 }}><span style={{ color: '#9ca3af', fontWeight: 600 }}>Angle: </span><span>{img.adAngle}</span></div>
                )}
                {img.headline && (
                  <div style={{ marginBottom: 3 }}><span style={{ color: '#9ca3af', fontWeight: 600 }}>Headline: </span><span>{img.headline}</span></div>
                )}
                {img.ctaText && (
                  <div style={{ marginBottom: 6 }}><span style={{ color: '#9ca3af', fontWeight: 600 }}>CTA: </span><span>{img.ctaText}</span></div>
                )}
                {img.wavespeedPrompt && (
                  <div>
                    <div style={{ color: '#9ca3af', fontWeight: 600, marginBottom: 3 }}>Prompt:</div>
                    <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 4, padding: '5px 7px', fontSize: 10.5, color: '#d1d5db', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {img.wavespeedPrompt}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StyleAngleLabel({ imageId }: { imageId: string }) {
  const images = useActiveProjectImages();
  const img = images.find((i) => i.id === imageId);

  if (!img || (!img.adStyle && !img.adAngle)) return null;

  const parts: string[] = [];
  if (img.adStyle) parts.push(img.adStyle);
  if (img.adAngle) parts.push(img.adAngle);

  return (
    <div
      style={{
        fontSize: 9,
        color: '#9ca3af',
        lineHeight: 1.2,
        maxWidth: '70%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 500,
        letterSpacing: '0.01em',
      }}
    >
      {parts.join(' · ')}
    </div>
  );
}

export interface ImageNodeData {
  src: string;
  prompt?: string;
  displayWidth: number;
  displayHeight: number;
  isFavorite?: boolean;
  /** Playable tile (video-ad-clone): src = poster frame, videoUrl = mp4.
   *  When set, the media area renders VideoTile instead of the plain <img>. */
  videoUrl?: string | null;
}

/**
 * Playable video tile (video-ad-clone). Poster-first: shows the tile's still
 * frame (src on the canvas_images row) with a centered play button; clicking
 * swaps to a native <video controls autoPlay>. When playback ends we return
 * to the poster state. Tiles WITHOUT videoUrl never reach this component —
 * the plain <img> branch in ImageNode is untouched.
 */
function VideoTile({ src, poster, imageId }: { src: string; poster: string; imageId: string }) {
  // v5.17 — hover-play with sound (Dan: no play button on canvas). Mirrors
  // the Ad Library's HoverVideoCard behavior via the shared hover-sound pref:
  // hover starts playback (200ms intent delay so canvas panning doesn't
  // trigger drive-by audio), leave stops+rewinds, speaker toggles the GLOBAL
  // preference. Unmuted play() can reject before any qualifying gesture —
  // fall back to muted rather than a dead tile.
  const [playing, setPlaying] = useState(false);
  const [soundOn, setSoundOn] = useState(isHoverSoundEnabled());
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirrors the plain-img branch's imgFailed treatment: a dead poster URL
  // renders the same failure visual instead of the browser's broken-image
  // glyph. Hover-play stays functional — the mp4 may still play fine
  // even when the poster frame 404s.
  const [posterFailed, setPosterFailed] = useState(false);

  // Return to poster if the video source changes out from under us
  // (e.g. a re-clone replaced the mp4 on this tile).
  useEffect(() => {
    setPlaying(false);
  }, [src]);

  // Reset failure state when the poster changes (retry / new still frame).
  useEffect(() => {
    setPosterFailed(false);
  }, [poster]);

  // Never leak a pending hover-intent timer on unmount.
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  const playMutedFallback = (v: HTMLVideoElement) => {
    v.muted = true;
    setSoundOn(false);
    v.play().catch(() => {});
  };

  const handleEnter = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setSoundOn(isHoverSoundEnabled());
      setPlaying(true);
    }, 200);
  };

  const handleLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
    setPlaying(false);
  };

  const toggleSound = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !soundOn;
    setHoverSoundEnabled(next);
    setSoundOn(next);
    const v = videoRef.current;
    if (!v) return;
    v.muted = !next; // click is the gesture — unmute sticks
    if (next && v.paused && playing) v.play().catch(() => playMutedFallback(v));
  };

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {playing ? (
        <>
          {/* v6.04 — NO nodrag / mousedown-swallow on the video surface:
              hover always flips the tile to this element (200ms), so marking
              it nodrag made video tiles permanently un-draggable (Dan: "why
              are the videos not movable?"). There are no native controls on
              it — react-flow drag needs the mousedown. */}
          <video
            data-testid={`video-tile-player-${imageId}`}
            ref={(el) => {
              videoRef.current = el;
              if (el) {
                el.muted = !isHoverSoundEnabled();
                el.play().catch(() => {
                  if (!el.muted) playMutedFallback(el);
                });
              }
            }}
            src={src}
            poster={poster}
            playsInline
            loop
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <button
            className="nodrag"
            data-testid={`video-tile-mute-${imageId}`}
            data-sound={soundOn ? 'on' : 'off'}
            title={soundOn ? 'Mute' : 'Unmute'}
            aria-label={soundOn ? 'Mute hover videos' : 'Unmute hover videos'}
            onClick={toggleSound}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              width: 28,
              height: 28,
              border: 0,
              padding: 0,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 3,
            }}
          >
            {soundOn ? <Volume2 size={15} color="#fff" /> : <VolumeX size={15} color="#fff" />}
          </button>
        </>
      ) : (
        <>
          {posterFailed ? (
            <div
              data-testid={`video-poster-failed-${imageId}`}
              style={{
                width: '100%',
                height: '100%',
                background: '#FEF2F2',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: 16,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 24, color: '#EF4444', lineHeight: 1 }}>✕</div>
              <div style={{ fontSize: 12, color: '#EF4444', fontWeight: 600 }}>Image failed to load</div>
              <div style={{ fontSize: 10, color: '#9CA3AF' }}>The poster frame is no longer available — the video may still play.</div>
            </div>
          ) : (
            <img
              src={poster}
              alt=""
              draggable={false}
              onError={() => setPosterFailed(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          )}
          {/* v5.17 — small non-interactive Play badge as the "this is a video"
              affordance; playback is hover-driven (no click needed). Kept
              small + corner-parked so it never reads as a button. */}
          <div
            data-testid={`video-tile-badge-${imageId}`}
            style={{
              position: 'absolute',
              bottom: 6,
              left: 6,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Play size={12} color="#fff" fill="#fff" style={{ marginLeft: 1 }} />
          </div>
        </>
      )}
    </div>
  );
}

interface MenuAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  testId: string;
  danger?: boolean;
}

function FacebookAdPreview({ imageId, src, displayWidth, displayHeight, isGenerating }: {
  imageId: string;
  src: string;
  displayWidth: number;
  displayHeight: number;
  isGenerating?: boolean;
}) {
  const canvasAdCopy = useActiveProjectCanvasAdCopy();
  const setCanvasAdCopyActiveIndex = useCanvasStore((s) => s.setCanvasAdCopyActiveIndex);
  const adCopyState = canvasAdCopy[imageId];
  const variations = adCopyState?.variations || [];
  const activeIdx = adCopyState?.activeVariationIndex || 0;
  const activeVariation = variations[activeIdx];

  const hasVariations = variations.length > 0;
  const displayedHeadline = activeVariation?.headline || (isGenerating ? "Generating headline…" : "Your headline goes here");
  const displayedHook = activeVariation?.hook || (isGenerating ? "Crafting hook…" : "");
  const displayedPrimaryText = activeVariation?.primaryText || (isGenerating ? "Writing compelling ad copy…" : "Your primary text will appear here once copy is generated.");
  const displayedCta = activeVariation?.cta || "Shop Now";
  const variationLabel = hasVariations ? `Copy ${activeIdx + 1} of ${variations.length}` : isGenerating ? "Generating…" : "No variations";

  const handlePrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeIdx > 0) setCanvasAdCopyActiveIndex(imageId, activeIdx - 1);
  }, [imageId, activeIdx, setCanvasAdCopyActiveIndex]);

  const handleNext = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeIdx < variations.length - 1) setCanvasAdCopyActiveIndex(imageId, activeIdx + 1);
  }, [imageId, activeIdx, variations.length, setCanvasAdCopyActiveIndex]);

  const scale = Math.min(displayWidth / 320, 1);

  return (
    <div
      style={{
        width: displayWidth,
        height: displayHeight,
        background: '#F0F2F5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflow: 'hidden',
        fontSize: scale < 0.9 ? 10 : 13,
      }}
    >
      <div style={{
        background: '#fff',
        borderRadius: 8,
        overflow: 'hidden',
        margin: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        height: displayHeight - 16,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', flexShrink: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 700, fontSize: 14,
          }}>B</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: Math.max(11, 13 * scale), color: '#1c1e21', lineHeight: 1.2 }}>Brand Page</div>
            <div style={{ fontSize: Math.max(9, 10 * scale), color: '#606770', display: 'flex', alignItems: 'center', gap: 3 }}>
              <span>Sponsored</span>
              <span style={{ fontSize: 4 }}>●</span>
              <span>🌐</span>
            </div>
          </div>
          <div style={{ color: '#606770', fontSize: 18 }}>···</div>
        </div>

        {/* Primary text with hook as opening line */}
        <div style={{ padding: '0 10px 8px', fontSize: Math.max(10, 12 * scale), color: '#1c1e21', lineHeight: 1.45, flexShrink: 0, maxHeight: 70, overflow: 'hidden' }}>
          {displayedHook && (
            <span style={{ fontWeight: 700, display: 'block', marginBottom: 2, color: '#1c1e21' }}>{displayedHook} </span>
          )}
          {displayedPrimaryText}
          {(displayedHook + displayedPrimaryText).length > 100 && (
            <span style={{ color: '#0866ff', cursor: 'pointer', fontWeight: 600 }}> See more</span>
          )}
        </div>

        {/* Ad image */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: 80 }}>
          <img
            src={src}
            alt=""
            onError={(e) => {
              // Hide the broken image and show a placeholder background
              const target = e.currentTarget;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent && !parent.querySelector('[data-failed-marker]')) {
                const failed = document.createElement('div');
                failed.setAttribute('data-failed-marker', '1');
                failed.style.cssText = 'width:100%;height:100%;background:#FEF2F2;display:flex;align-items:center;justify-content:center;color:#EF4444;font-size:11px;font-weight:600;';
                failed.textContent = 'Image failed to load';
                parent.appendChild(failed);
              }
            }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            draggable={false}
          />
        </div>

        {/* Below-image row: domain + headline + CTA */}
        <div style={{
          background: '#f0f2f5',
          padding: '8px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: Math.max(8, 9 * scale), color: '#606770', textTransform: 'uppercase', marginBottom: 2 }}>brand.com</div>
            <div style={{
              fontWeight: 700,
              fontSize: Math.max(10, 13 * scale),
              color: '#1c1e21',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>{displayedHeadline}</div>
          </div>
          <button
            className="nodrag"
            style={{
              background: '#e4e6eb',
              border: 'none',
              borderRadius: 6,
              padding: '6px 12px',
              fontWeight: 700,
              fontSize: Math.max(9, 12 * scale),
              color: '#1c1e21',
              cursor: 'default',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >{displayedCta}</button>
        </div>

        {/* Engagement row */}
        <div style={{ padding: '6px 10px', borderTop: '1px solid #e4e6eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: Math.max(9, 11 * scale), color: '#606770' }}>
            <span>👍 ❤️ 128</span>
          </div>
          <div style={{ fontSize: Math.max(9, 11 * scale), color: '#606770' }}>24 comments · 8 shares</div>
        </div>

        {/* Action row */}
        <div style={{ display: 'flex', borderTop: '1px solid #e4e6eb', flexShrink: 0 }}>
          {['👍 Like', '💬 Comment', '↗ Share'].map((action) => (
            <div key={action} style={{
              flex: 1, textAlign: 'center',
              padding: '6px 4px',
              fontSize: Math.max(9, 11 * scale),
              color: '#606770',
              fontWeight: 600,
            }}>{action}</div>
          ))}
        </div>

        {/* Variation cycling controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '4px 8px',
          background: isGenerating ? 'rgba(245,158,11,0.08)' : 'rgba(99,102,241,0.08)',
          borderTop: `1px solid ${isGenerating ? 'rgba(245,158,11,0.25)' : 'rgba(99,102,241,0.2)'}`,
          flexShrink: 0,
        }}>
          {isGenerating && !hasVariations ? (
            <span style={{ fontSize: Math.max(9, 11 * scale), color: '#D97706', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
              {variationLabel}
            </span>
          ) : (
            <>
              <button
                className="nodrag"
                data-testid={`copy-prev-${imageId}`}
                onClick={handlePrev}
                disabled={activeIdx === 0}
                style={{
                  background: 'none', border: 'none', cursor: activeIdx === 0 ? 'not-allowed' : 'pointer',
                  padding: 2, color: activeIdx === 0 ? '#ccc' : '#6366f1', display: 'flex',
                }}
              >
                <ChevronLeft size={14} />
              </button>
              <span style={{ fontSize: Math.max(9, 11 * scale), color: '#6366f1', fontWeight: 600 }}>
                {variationLabel}
              </span>
              <button
                className="nodrag"
                data-testid={`copy-next-${imageId}`}
                onClick={handleNext}
                disabled={activeIdx >= variations.length - 1}
                style={{
                  background: 'none', border: 'none', cursor: activeIdx >= variations.length - 1 ? 'not-allowed' : 'pointer',
                  padding: 2, color: activeIdx >= variations.length - 1 ? '#ccc' : '#6366f1', display: 'flex',
                }}
              >
                <ChevronRight size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** v6.54 — subscribes to captionBurning so the overlay mounts/unmounts
 *  reactively (getState() reads inside render don't re-render). */
function CaptionBurnOverlay({ imageId }: { imageId: string }) {
  const label = useCanvasStore((s) => s.captionBurning[imageId]);
  if (!label) return null;
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: 6,
        background: 'rgba(10,10,10,0.62)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        zIndex: 9,
        pointerEvents: 'none',
      }}
    >
      <RefreshCw size={20} color="#fff" className="animate-spin" />
      <span style={{ color: '#fff', fontSize: 12.5, fontWeight: 600 }}>{label}</span>
    </div>
  );
}

export function ImageNode({ id, data, selected }: NodeProps) {
  const { deleteImage, sendAgentMessage, setSelectedImageIds, adCopyGeneratingImageIds, duplicateImage } = useCanvasStore();
  const activeProjectId = useCanvasStore((s) => s.activeProjectId);
  // Preserve overlays for active or background editor sessions.
  const overlayVisible = useCreativeEditStore((s) => {
    const p = s.prepares[id];
    return !!p && (p.phase !== 'ready' || s.activeImageId === id);
  });
  const layersOverlayVisible = useCreativeLayersStore((s) => {
    const p = s.prepares[id];
    return !!p && (p.phase !== 'ready' || s.activeImageId === id);
  });
  const user = useAuthUser();
  const isAdmin = !!user?.isAdmin;
  const { toast } = useToast();
  const { data: editorCfg } = useQuery<{ enabled: boolean }>({ queryKey: ['/api/assets/editor-enabled'] });
  const videoEditorEnabled = editorCfg?.enabled ?? false;
  const canvasAdCopy = useActiveProjectCanvasAdCopy();
  const d = data as unknown as ImageNodeData;
  // Reserve room for all five actions plus the optional preview toggle.
  // These dimensions live in canvas space, so the row stays inside the tile at any zoom.
  const hoverButtonSize = Math.min(22, Math.max(1, (d.displayWidth - 36) / 6));
  const hoverButtonStep = hoverButtonSize + 4;
  const hoverIconSize = Math.min(11, hoverButtonSize * 0.55);
  const [hovered, setHovered] = useState(false);
  const [actionsFocused, setActionsFocused] = useState(false);
  const [actionHelpOpen, setActionHelpOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  // Existing repair/video operations retain their agent/clone progress flow.
  const [regenerating, setRegenerating] = useState(false);
  // `imgFailed` flips to true when the <img> onError fires (broken src URL).
  // Without this we'd render `<img alt={prompt}>` which leaks the full
  // wavespeed prompt as visible alt text when the CDN URL fails to load.
  const [imgFailed, setImgFailed] = useState(false);
  // v3.16 — smooth placeholder→image swap. canvas_image_ready removes the grey
  // PlaceholderNode (#F1F5F9 shimmer) and mounts THIS node, whose <img> hasn't
  // loaded yet — over the white node background that's a jarring white flash /
  // "box disappears then image pops in". Hold a matching grey shimmer backdrop
  // until the image actually loads, then fade it in. So the tile stays grey
  // continuously from placeholder → loaded image, then the ad fades in.
  const [imgLoaded, setImgLoaded] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuInteractedOutside = useRef(false);

  // Reset failure/loaded state when the src changes (e.g. retry / new image).
  useEffect(() => {
    setImgFailed(false);
    setImgLoaded(false);
  }, [d.src]);

  const hasAdCopy = !!(canvasAdCopy[id]?.variations?.length);
  const isGenerating = adCopyGeneratingImageIds.has(id);
  const shouldShowPreview = hasAdCopy || isGenerating;

  // Auto-enter preview mode when copy generation starts or finishes
  useEffect(() => {
    if (shouldShowPreview && !isPreviewMode) {
      setIsPreviewMode(true);
    }
  }, [shouldShowPreview]);

  const handleDownload = useCallback(async () => {
    // Video tiles keep their real MP4 download; d.src is only the poster.
    if (d.videoUrl) {
      const a = document.createElement('a');
      a.href = d.videoUrl;
      a.download = `video-${id}.mp4`;
      a.click();
      return;
    }
    if (!activeProjectId) {
      toast({ title: 'Download failed', description: 'Open a saved project before downloading this image.', variant: 'destructive' });
      return;
    }
    // Static images use the same-origin server export, never a CDN anchor fallback.
    const result = await downloadCreatives(activeProjectId, [id]);
    if (!result.ok) {
      toast({ title: 'Download failed', description: result.error ?? 'Could not download this image.', variant: 'destructive' });
    }
  }, [d.videoUrl, id, activeProjectId, toast]);

  // Repair keeps packaging correction; static-ad Regenerate replays the saved request.
  // v5.38 — VIDEO tiles get their own regenerate semantics: a clip tile
  // regenerates ITS clip (reset-clip + generate; stops before the stitch per
  // the review flow), the final tile re-stitches. The image fix-packaging
  // agent flow must never run on a video tile (it was restyling the poster
  // frame through the IMAGE pipeline — the wrong-pipeline class).
  // Source-video tiles are deliberately OUTSIDE videoCloneTileIndex (v5.21),
  // so they resolve to kind null here and the regenerate button hides.
  const videoTileLink = d.videoUrl ? videoCloneTileIndex.get(d.src) : undefined;
  // v5.82 — ANY tile of a video-clone run (opening frame, clip, final): these
  // carry the run's own controls (scene-details Play at right:108, regen with
  // clone semantics). Creative-Edit + Duplicate must not render on them — the
  // Edit button landed on the Play button's slot and hid the path to the
  // next step (Dan: "the play button is now missing").
  const isVideoCloneTile = !!videoCloneTileIndex.get(d.src);
  const isStaticAd = !d.videoUrl && !isVideoCloneTile;
  const {regenerate: regenerateOriginal, busy: replaying} = useImageRegeneration(activeProjectId,id,isStaticAd);
  const videoRegenKind: 'clip' | 'final' | null = d.videoUrl && videoTileLink
    ? (videoTileLink.sceneIndex != null ? 'clip' : 'final')
    : null;

  const handleRegenerate = useCallback(() => {
    if (regenerating) return;
    if (d.videoUrl) {
      if (!videoTileLink || activeProjectId == null) return;
      setRegenerating(true);
      const run = videoTileLink.sceneIndex != null
        ? regenerateVideoCloneClip(videoTileLink.draftId, videoTileLink.sceneIndex, activeProjectId)
        : restitchVideoCloneFinal(videoTileLink.draftId, activeProjectId);
      run
        .catch((err) => console.error('[ImageNode] video regenerate failed:', err))
        .finally(() => setRegenerating(false));
      return;
    }
    // v6.01 — OPENING-FRAME tiles (image tiles of a clone run, no videoUrl):
    // regen must re-run the clone's frame pipeline, not the generic
    // fix-packaging agent (Dan's regen click produced a random 1:1 ad
    // variation — the wrong-pipeline class, image edition).
    const frameTileLink = videoCloneTileIndex.get(d.src);
    if (frameTileLink && frameTileLink.sceneIndex != null) {
      // Source-strip originals (tile=original-N) are reference tiles — no
      // regen semantics at all (and never the generic agent flow either).
      if (d.src.includes('tile=original')) return;
      if (activeProjectId == null) return;
      setRegenerating(true);
      const aspect = d.displayHeight >= d.displayWidth ? '9:16' : '16:9';
      regenerateVideoCloneFrame(frameTileLink.draftId, frameTileLink.sceneIndex, activeProjectId, aspect)
        .catch((err) => console.error('[ImageNode] frame regenerate failed:', err))
        .finally(() => setRegenerating(false));
      return;
    }
    setRegenerating(true);
    // Pin the selection to JUST this image so the agent's referenceImageIds
    // resolves to a single source. Even if the user had others selected, the
    // intent of clicking this image's regen button is "fix THIS one."
    setSelectedImageIds([id]);
    sendAgentMessage('fix packaging on this ad', undefined, { silent: true })
      .catch((err) => console.error('[ImageNode] regenerate failed:', err))
      .finally(() => setRegenerating(false));
    // Clear the selection immediately AFTER sendAgentMessage's synchronous
    // state snapshot. Otherwise the next user prompt (e.g. "make 5 fresh ads
    // of X") inherits this image as a reference and the agent routes to
    // run_variations_v3 instead of run_campaign_v2.
    setSelectedImageIds([]);
  }, [id, regenerating, setSelectedImageIds, sendAgentMessage, d.videoUrl, d.src, d.displayWidth, d.displayHeight, videoTileLink, activeProjectId]);

  // v6.20 — open the video editor (OpenCut fork) on this tile's mp4. The
  // blank window opens synchronously so popup blockers see a user gesture;
  // the session endpoint then supplies the /import URL to point it at.
  const handleEditVideo = useCallback(async () => {
    if (!d.videoUrl || activeProjectId == null) return;
    const win = window.open('', '_blank');
    try {
      const res = await fetch('/api/assets/editor-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId: activeProjectId,
          videoUrl: d.videoUrl,
          src: d.src,
          posterUrl: d.src,
          width: d.displayWidth,
          height: d.displayHeight,
        }),
      });
      const session = await res.json();
      if (!res.ok || !session.editorUrl) throw new Error(session.error || 'Failed to open editor');
      if (win) win.location.href = session.editorUrl;
      else window.open(session.editorUrl, '_blank');
    } catch (err) {
      win?.close();
      console.error('[edit-video] session failed:', err);
      toast({ title: 'Could not open editor', description: err instanceof Error ? err.message : undefined, variant: 'destructive' });
    }
  }, [d.videoUrl, d.src, d.displayWidth, d.displayHeight, activeProjectId, toast]);

  const menuActions: MenuAction[] = [
    {
      label: 'Download',
      icon: <Download size={13} />,
      onClick: () => { void handleDownload(); setMenuOpen(false); },
      testId: `node-menu-download-${id}`,
    },
    ...(d.videoUrl && videoEditorEnabled ? [{
      label: 'Edit video',
      icon: <Film size={13} />,
      onClick: () => { void handleEditVideo(); setMenuOpen(false); },
      testId: `node-menu-edit-video-${id}`,
    }] : []),
    ...(!d.videoUrl && !isVideoCloneTile ? [{
      label: 'Duplicate',
      icon: <Copy size={13} />,
      onClick: () => { duplicateImage(id); setMenuOpen(false); },
      testId: `node-menu-duplicate-${id}`,
    }] : []),
    {
      label: 'Delete',
      icon: <Trash2 size={13} />,
      onClick: () => { deleteImage(id); setMenuOpen(false); },
      testId: `node-menu-delete-${id}`,
      danger: true,
    },
  ];

  return (
    <div
      tabIndex={0}
      role="group"
      aria-label="Ad creative actions"
      onFocusCapture={() => setActionsFocused(true)}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setActionsFocused(false);
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: d.displayWidth,
        height: d.displayHeight,
        borderRadius: 8,
        border: selected
          ? '2px solid #6366F1'
          : '1.5px solid #DCE3ED',
        background: '#fff',
        boxShadow: selected
          ? '0 0 0 3px rgba(99,102,241,0.18), 0 4px 24px rgba(0,0,0,0.10)'
          : '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: 6 }}>
        {overlayVisible ? (
          <CreativeEditNodeOverlay imageId={id} width={d.displayWidth} height={d.displayHeight} />
        ) : layersOverlayVisible ? (
          <CreativeLayersNodeOverlay imageId={id} width={d.displayWidth} height={d.displayHeight} />
        ) : isPreviewMode ? (
          <FacebookAdPreview
            imageId={id}
            src={d.src}
            displayWidth={d.displayWidth}
            displayHeight={d.displayHeight}
            isGenerating={isGenerating}
          />
        ) : imgFailed ? (
          // Broken src (WaveSpeed CDN URL expired / 404 / network error). Render
          // a clean failure state instead of letting the browser fall back to
          // the alt text — which would dump the full wavespeed prompt onto the
          // canvas tile.
          <div
            data-testid={`image-load-failed-${id}`}
            style={{
              width: '100%',
              height: '100%',
              background: '#FEF2F2',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: 16,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 24, color: '#EF4444', lineHeight: 1 }}>✕</div>
            <div style={{ fontSize: 12, color: '#EF4444', fontWeight: 600 }}>Image failed to load</div>
            <div style={{ fontSize: 10, color: '#9CA3AF' }}>The generated image URL is no longer available.</div>
          </div>
        ) : d.videoUrl ? (
          // Playable video tile (video-ad-clone): poster + play button →
          // native <video>. Tiles without videoUrl fall through to the
          // untouched <img> branch below.
          <VideoTile src={d.videoUrl} poster={d.src} imageId={id} />
        ) : (
          <div style={{ width: '100%', height: '100%', position: 'relative', background: '#EAF0F7' }}>
            {/* Loading state — identical "Generating…" tile to PlaceholderNode
                (v3.22). An arrived-but-not-yet-downloaded image used to show a
                plain grey backdrop with no spinner, which reads as a blank space
                in the grid (user: "still a bunch of blank spaces… there should
                never be blank space"). Now every not-yet-ready slot — pending
                placeholder OR still-downloading image — shows the same spinner. */}
            {!imgLoaded && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  background: 'linear-gradient(100deg, #EAF0F7 30%, #DCE3ED 50%, #EAF0F7 70%)',
                  backgroundSize: '200% 100%',
                  animation: 'imgnode-load-shimmer 1.6s ease-in-out infinite',
                }}
              >
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: '2.5px solid #C8D3E2',
                    borderTopColor: '#5F6D82',
                    animation: 'imgnode-spin 0.8s linear infinite',
                  }}
                />
                <div style={{ fontSize: 11, color: '#5F6D82', fontWeight: 500 }}>Generating…</div>
              </div>
            )}
            <img
              src={d.src}
              alt=""
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgFailed(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                opacity: imgLoaded ? 1 : 0,
                transition: 'opacity 260ms ease',
              }}
              draggable={false}
            />
            <style>{`@keyframes imgnode-load-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } } @keyframes imgnode-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>

      {/* Style/Angle label + QA badge + Ad info badge — positioned below the image.
          `marginLeft: auto` on the right group pins the QA + info badges to the
          right edge regardless of whether StyleAngleLabel rendered. Without it,
          ads with no adStyle/adAngle metadata (older rows or SSE-drop recoveries
          where the columns came back null) had no left flex child, and
          space-between fell back to flex-start — leaving the QA badge on the
          LEFT for those tiles while ads with metadata kept it on the right.
          User reported this as "the QA is on the right for some, left for others." */}
      <div
        className="nokey"
        style={{
          position: 'absolute',
          bottom: -CANVAS_AD_METADATA_HEIGHT,
          left: 4,
          right: 4,
          display: 'flex',
          alignItems: 'center',
          zIndex: 15,
        }}
      >
        <StyleAngleLabel imageId={id} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
          <AdMetaBadges src={d.src} imageId={id} />
        </div>
      </div>

      {/* Ad preview toggle — show when in preview mode or has copy / generating.
          v5.38: never on video tiles (a Facebook IMAGE-ad preview of a video's
          poster frame is meaningless). */}
      {(hovered || actionsFocused || actionHelpOpen || selected) && !overlayVisible && (shouldShowPreview || isPreviewMode) && !d.videoUrl && (
        <button
          className="nodrag nokey"
          data-testid={`node-preview-toggle-${id}`}
          title={isPreviewMode ? 'Exit ad preview' : 'View as Facebook ad'}
          onClick={(e) => { e.stopPropagation(); setIsPreviewMode((v) => !v); }}
          style={{
            position: 'absolute',
            top: 6,
            left: 6,
            width: hoverButtonSize,
            height: hoverButtonSize,
            borderRadius: 5,
            padding: 0,
            border: '1px solid rgba(229,226,220,0.8)',
            background: isPreviewMode ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: isPreviewMode ? '#6366f1' : '#1A1A1A',
            zIndex: 10,
          }}
        >
          <Monitor size={hoverIconSize} />
        </button>
      )}

      {/* Scene details (v5.13) — only on video-clone tiles (src is in the
          runner's videoCloneTileIndex; admin-only in practice since the index
          only fills for admins). Opens the script doc with this tile's scene
          expanded. Styled like VideoTile's play button (dark circle, white
          Play glyph) at the hover-toolbar's compact size, continuing the
          top-right button row leftward after menu, regeneration and debug. */}
      {(hovered || actionsFocused || actionHelpOpen || selected) && (() => {
        const sceneLink = videoCloneTileIndex.get(d.src);
        if (!sceneLink) return null;
        return (
          <button
            className="nodrag nokey"
            data-testid={`node-scene-details-${id}`}
            title="Scene details"
            aria-label="Scene details"
            onClick={(e) => {
              e.stopPropagation();
              useCanvasStore.getState().openSidebarPanel({
                type: 'video_script',
                title: 'Video Script',
                draftId: sceneLink.draftId,
                expandScene: sceneLink.sceneIndex,
              });
            }}
            style={{
              position: 'absolute',
              top: 6,
              right: 6 + hoverButtonStep * 3,
              width: hoverButtonSize,
              height: hoverButtonSize,
              border: 0,
              padding: 0,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
              zIndex: 10,
            }}
          >
            {/* marginLeft nudges the triangle to the circle's optical center */}
            <Play size={hoverIconSize} color="#fff" fill="#fff" style={{ marginLeft: 1 }} />
          </button>
        );
      })()}

      {/* v6.54 — caption burn overlay: the tile ITSELF is the progress UI
          ("update the existing video, not generate a new one"). */}
      <CaptionBurnOverlay imageId={id} />

      {/* v6.53 — Captions (TikTok-style dialog captions): GENERATED VIDEO
          tiles only (videoUrl + linked to a clone draft). Opens the CapCut-
          style preset picker; the captioned video lands BESIDE this tile.
          Continues the compact hover row leftward after the scene control. */}
      {(hovered || actionsFocused || actionHelpOpen || selected) && d.videoUrl && (() => {
        const capLink = videoCloneTileIndex.get(d.src);
        if (!capLink) return null;
        return (
          <button
            className="nodrag nokey"
            data-testid={`node-captions-${id}`}
            title="Add TikTok-style captions"
            aria-label="Add TikTok-style captions"
            onClick={(e) => {
              e.stopPropagation();
              useCanvasStore.getState().setCaptionPicker({ imageId: id, draftId: capLink.draftId, videoUrl: d.videoUrl! });
            }}
            style={{
              position: 'absolute',
              top: 6,
              right: 6 + hoverButtonStep * 4,
              width: hoverButtonSize,
              height: hoverButtonSize,
              border: 0,
              padding: 0,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
              zIndex: 10,
            }}
          >
            <Captions size={hoverIconSize} color="#fff" />
          </button>
        );
      })()}

      {/* Admin-only debug snapshot. */}
      {isAdmin && (hovered || actionsFocused || actionHelpOpen || selected) && !overlayVisible && activeProjectId != null && (
        <div
          className="nodrag nokey"
          style={{ position: 'absolute', top: 6, right: 6 + hoverButtonStep * (isStaticAd ? 3 : 2), zIndex: 10 }}
        >
          <DebugSnapshotPopover
            clientId={id}
            projectId={activeProjectId}
            trigger={
              <CanvasActionButton actionLabel="Inspect ad details" description="Review technical details and copy a diagnostic snapshot for troubleshooting. Admin only." onHelpOpenChange={setActionHelpOpen}
                data-testid={`node-debug-snapshot-${id}`}
                onClick={(e) => { e.stopPropagation(); }}
                style={{
                  width: hoverButtonSize,
                  height: hoverButtonSize,
                  borderRadius: 5,
                  padding: 0,
                  border: '1px solid rgba(229,226,220,0.8)',
                  background: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#1A1A1A',
                }}
              >
                <Code2 size={hoverIconSize} />
              </CanvasActionButton>
            }
          />
        </div>
      )}

      {/* v5.77 — Duplicate: copies the whole creative to the end of its row. */}
      {(hovered || actionsFocused || actionHelpOpen || selected) && !overlayVisible && !layersOverlayVisible && !d.videoUrl && !isVideoCloneTile && (
        <CanvasActionButton actionLabel="Duplicate ad" description="Add a copy of this creative to the end of its canvas row." onHelpOpenChange={setActionHelpOpen}
          data-testid={`node-duplicate-${id}`}
          className="nodrag nokey"
          onClick={(e) => { e.stopPropagation(); duplicateImage(id); }}
          style={{
            position: 'absolute',
            top: 6,
            right: 6 + hoverButtonStep * (isAdmin && activeProjectId != null ? 4 : 3),
            width: hoverButtonSize,
            height: hoverButtonSize,
            borderRadius: 5,
            padding: 0,
            border: '1px solid rgba(229,226,220,0.8)',
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#1A1A1A',
            zIndex: 10,
          }}
        >
          <Copy size={hoverIconSize} />
        </CanvasActionButton>
      )}

      {isStaticAd && (hovered || actionsFocused || actionHelpOpen || selected || replaying) && !overlayVisible && !layersOverlayVisible && (
        <CanvasActionButton actionLabel="Regenerate ad" description="Create a new result using this ad’s original prompt and references." onHelpOpenChange={setActionHelpOpen}
          data-testid={`node-regenerate-${id}`}
          className="nodrag nokey"
          aria-label="Regenerate ad"
          onClick={e=>{e.stopPropagation();void regenerateOriginal();}}
          disabled={replaying || regenerating}
          style={{position:'absolute',top:6,right:6 + hoverButtonStep,width:hoverButtonSize,height:hoverButtonSize,padding:0,borderRadius:5,
            border:'1px solid rgba(229,226,220,0.8)',background:replaying?'rgba(99,102,241,0.12)':'rgba(255,255,255,0.92)',
            backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',
            cursor:replaying?'wait':'pointer',color:replaying?'#6366f1':'#1A1A1A',zIndex:10}}
        ><RefreshCw size={hoverIconSize} style={replaying?{animation:'imgnode-spin 0.9s linear infinite'}:undefined}/></CanvasActionButton>
      )}

      {(hovered || actionsFocused || actionHelpOpen || selected || regenerating) && !overlayVisible && !layersOverlayVisible && !(d.videoUrl && videoRegenKind === null) && (
        <CanvasActionButton
          actionLabel={videoRegenKind === 'clip' ? 'Regenerate clip' : videoRegenKind === 'final' ? 'Rebuild final video' : isStaticAd ? 'Repair product packaging' : 'Regenerate opening frame'}
          description={isStaticAd ? 'Ask Admoji to correct the product packaging in this ad.' : videoRegenKind === 'final' ? 'Combine the existing scenes again to rebuild the final video.' : videoRegenKind === 'clip' ? 'Generate a new version of this video clip.' : 'Generate a new opening image for this scene.'}
          onHelpOpenChange={setActionHelpOpen}
          data-testid={isStaticAd ? `node-repair-${id}` : `node-regenerate-${id}`}
          aria-label={isStaticAd ? "Repair product packaging" : "Regenerate video asset"}
          className="nodrag nokey"
          onClick={(e) => { e.stopPropagation(); handleRegenerate(); }}
          disabled={regenerating || replaying}
          style={{
            position: 'absolute',
            top: 6,
            right: 6 + hoverButtonStep * (isStaticAd ? 2 : 1),
            width: hoverButtonSize,
            height: hoverButtonSize,
            borderRadius: 5,
            padding: 0,
            border: '1px solid rgba(229,226,220,0.8)',
            background: regenerating ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: regenerating ? 'wait' : 'pointer',
            color: regenerating ? '#6366f1' : '#1A1A1A',
            zIndex: 10,
          }}
        >
          {isStaticAd ? <Wrench size={hoverIconSize} /> : <RefreshCw
            size={hoverIconSize}
            style={regenerating ? { animation: 'imgnode-spin 0.9s linear infinite' } : undefined}
          />}
          <style>{`
            @keyframes imgnode-spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </CanvasActionButton>
      )}

      {/* Three-dot context menu */}
      {(hovered || actionsFocused || actionHelpOpen || selected || menuOpen) && !overlayVisible && (
        <div
          className="nodrag nokey"
          style={{ position: 'absolute', top: 6, right: 6, zIndex: 10 }}
        >
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverAnchor asChild>
              <CanvasActionButton actionLabel="More ad options" description="Open actions such as downloading, duplicating, or deleting this ad." onHelpOpenChange={setActionHelpOpen}
                ref={menuButtonRef}
                data-testid={`node-menu-btn-${id}`}
                className="nodrag nokey"
                aria-haspopup="dialog"
                aria-expanded={menuOpen}
                onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
                style={{
                  width: hoverButtonSize,
                  height: hoverButtonSize,
                  borderRadius: 5,
                  padding: 0,
                  border: '1px solid rgba(229,226,220,0.8)',
                  background: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#1A1A1A',
                  fontSize: hoverButtonSize * 0.65,
                  lineHeight: 1,
                  fontWeight: 700,
                  letterSpacing: 1,
                }}
              >
                ⋯
              </CanvasActionButton>
            </PopoverAnchor>

            {/* Escape the transformed ReactFlow stacking context: a node-local
                z-index cannot rise above the floating Canvas toolbar. */}
            <PopoverContent side="bottom" align="end" sideOffset={4} collisionPadding={12}
              className="nodrag nokey nowheel"
              data-testid={`node-menu-${id}`}
              aria-label="Ad actions"
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
              onDoubleClick={event => event.stopPropagation()}
              onOpenAutoFocus={() => { menuInteractedOutside.current = false; }}
              onInteractOutside={event => {
                // The anchor owns its click toggle. Dismissing on pointer-down
                // first can commit a close before the same click reopens it.
                if (menuButtonRef.current?.contains(event.detail.originalEvent.target as Node)) {
                  event.preventDefault();
                  return;
                }
                menuInteractedOutside.current = true;
              }}
              onCloseAutoFocus={event => {
                event.preventDefault();
                if (!menuInteractedOutside.current) menuButtonRef.current?.focus();
              }}
              style={{
                background: '#FFFFFF',
                border: '1px solid #DCE3ED',
                borderRadius: 4,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                width: 160,
                padding: 0,
                zIndex: 1600,
                maxHeight: 'var(--radix-popover-content-available-height)',
                overflowY: 'auto',
              }}
            >
              {menuActions.map((item) => (
                <button
                  key={item.label}
                  data-testid={item.testId}
                  className="nodrag nokey"
                  onClick={(e) => { e.stopPropagation(); item.onClick(); }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: item.danger ? '#EF4444' : '#1A1A1A',
                    fontSize: 13,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#F5F5F4';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
